/**
 * Template Management — shadcn/ui redesign
 *
 * Visual changes only — all state, API calls, and handlers are preserved exactly.
 * Replaced: MUI → shadcn Card/Table/Sheet/AlertDialog/DropdownMenu/Badge/Select/Input
 * Added:    KPI strip (computed from templates array), per-row DropdownMenu,
 *           Sheet for TemplateBuilder, Skeleton loading rows, AlertDialog for delete.
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Send,
  RefreshCw,
  MoreHorizontal,
  CheckCircle2,
  XCircle,
  Clock,
  FileEdit,
  List,
  Copy,
  Search,
  LayoutList,
  AlertTriangle,
  X,
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
} from 'lucide-react';
import {
  WhatsAppTemplate,
  WhatsAppTemplateStatus,
  WhatsAppTemplateCategory,
  TemplateCreateRequest,
} from '../../types/whatsapp-templates';
import whatsappTemplatesApi from '../../services/whatsapp-templates-api';
import TemplateBuilder from './TemplateBuilder';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Skeleton } from '../ui/skeleton';
import { ScrollArea } from '../ui/scroll-area';
import { Alert, AlertDescription } from '../ui/alert';
import { cn } from '../../lib/utils';

// ─── Status & Category display config ─────────────────────────────────────────
const STATUS_CONFIG: Record<
  WhatsAppTemplateStatus,
  { label: string; Icon: React.ComponentType<{ className?: string }>; className: string }
> = {
  [WhatsAppTemplateStatus.DRAFT]:    { label: 'Draft',    Icon: FileEdit,     className: 'border-border text-muted-foreground' },
  [WhatsAppTemplateStatus.PENDING]:  { label: 'Pending',  Icon: Clock,        className: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300' },
  [WhatsAppTemplateStatus.APPROVED]: { label: 'Approved', Icon: CheckCircle2, className: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' },
  [WhatsAppTemplateStatus.REJECTED]: { label: 'Rejected', Icon: XCircle,      className: 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300' },
  [WhatsAppTemplateStatus.DELETED]:  { label: 'Deleted',  Icon: Trash2,       className: 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-300' },
};

const CATEGORY_CONFIG: Record<
  WhatsAppTemplateCategory,
  { label: string; className: string }
> = {
  [WhatsAppTemplateCategory.MARKETING]:      { label: 'Marketing',      className: 'border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-800 dark:bg-violet-950 dark:text-violet-300' },
  [WhatsAppTemplateCategory.UTILITY]:        { label: 'Utility',        className: 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300' },
  [WhatsAppTemplateCategory.AUTHENTICATION]: { label: 'Authentication', className: 'border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-950 dark:text-orange-300' },
};

// ─── Component ────────────────────────────────────────────────────────────────
interface TemplateManagementProps {
  accountId: number;
  channelId?: number;
}

const TemplateManagement: React.FC<TemplateManagementProps> = ({ accountId, channelId }) => {
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // UI state
  const [builderOpen, setBuilderOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<WhatsAppTemplate | null>(null);
  const [syncing, setSyncing] = useState(false);

  // "Names on WhatsApp" dialog
  const [namesDialogOpen, setNamesDialogOpen] = useState(false);
  const [namesOnProvider, setNamesOnProvider] = useState<
    Array<{ name: string; language: string; status: string }>
  >([]);
  const [namesLoading, setNamesLoading] = useState(false);
  const [namesError, setNamesError] = useState<string | null>(null);

  // ── Pagination — derived from loaded templates ───────────────────────────────
  const totalPages = Math.max(1, Math.ceil(templates.length / pageSize));
  const paginatedTemplates = templates.slice((page - 1) * pageSize, page * pageSize);

  // ── KPI strip — derived from loaded templates (zero extra API calls) ─────────
  const kpis = useMemo(
    () => [
      { label: 'Total',    value: templates.length,                                                           Icon: LayoutList,   className: '' },
      { label: 'Approved', value: templates.filter((t) => t.status === WhatsAppTemplateStatus.APPROVED).length, Icon: CheckCircle2, className: 'text-emerald-700' },
      { label: 'Pending',  value: templates.filter((t) => t.status === WhatsAppTemplateStatus.PENDING).length,  Icon: Clock,        className: 'text-amber-700' },
      { label: 'Draft',    value: templates.filter((t) => t.status === WhatsAppTemplateStatus.DRAFT).length,    Icon: FileEdit,     className: 'text-muted-foreground' },
      { label: 'Rejected', value: templates.filter((t) => t.status === WhatsAppTemplateStatus.REJECTED).length, Icon: XCircle,      className: 'text-red-600' },
    ],
    [templates],
  );

  // ── Data loading ──────────────────────────────────────────────────────────────
  const loadTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {
        status:   statusFilter   || undefined,
        category: categoryFilter || undefined,
        search:   searchFilter   || undefined,
      };
      const response = await whatsappTemplatesApi.listTemplates(accountId, filters);
      if (response.success) {
        setTemplates(response.templates);
      } else {
        setError('Failed to load templates');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, statusFilter, categoryFilter, searchFilter]);

  // ── Handlers — all logic preserved exactly ────────────────────────────────────
  const handleCreateTemplate = async (templateData: TemplateCreateRequest) => {
    const response = await whatsappTemplatesApi.createTemplate(accountId, templateData);
    if (response.success) {
      setBuilderOpen(false);
      setSelectedTemplate(null);
      loadTemplates();
    }
  };

  const handleUpdateTemplate = async (templateData: TemplateCreateRequest) => {
    if (!selectedTemplate) return;
    const response = await whatsappTemplatesApi.updateTemplate(selectedTemplate.id, templateData);
    if (response.success) {
      setBuilderOpen(false);
      setSelectedTemplate(null);
      loadTemplates();
    }
  };

  const handleDeleteTemplate = async () => {
    if (!templateToDelete) return;
    try {
      await whatsappTemplatesApi.deleteTemplate(templateToDelete.id);
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      loadTemplates();
    } catch (err: any) {
      setError(err.message || 'Failed to delete template');
    }
  };

  const handleSubmitForApproval = async (template: WhatsAppTemplate) => {
    if (!channelId) { setError('No WhatsApp channel configured'); return; }
    try {
      const response = await whatsappTemplatesApi.submitForApproval(template.id, channelId);
      if (response.success) {
        loadTemplates();
        setError(null);
      } else {
        const msg = response.error || response.message || 'Failed to submit template';
        const isNameInUse = typeof msg === 'string' && /already in use|name.*in use/i.test(msg);
        setError(isNameInUse ? `${msg} Edit the template and choose a different name, then try again.` : msg);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to submit template';
      const isNameInUse = typeof msg === 'string' && /already in use|name.*in use/i.test(msg);
      setError(isNameInUse ? `${msg} Edit the template and choose a different name, then try again.` : msg);
    }
  };

  const handleSyncStatus = async (template: WhatsAppTemplate) => {
    if (!channelId) { setError('No WhatsApp channel configured'); return; }
    try {
      const response = await whatsappTemplatesApi.syncTemplateStatus(template.id, channelId);
      if (response.success) loadTemplates();
    } catch (err: any) {
      setError(err.message || 'Failed to sync template status');
    }
  };

  const handleSyncAll = async () => {
    if (!channelId) { setError('No WhatsApp channel configured'); return; }
    setSyncing(true);
    try {
      const response = await whatsappTemplatesApi.syncAllTemplates(channelId);
      if (response.success) {
        loadTemplates();
      } else {
        setError('Failed to sync templates');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to sync templates');
    } finally {
      setSyncing(false);
    }
  };

  const handleViewNamesOnWhatsApp = async () => {
    if (!channelId) { setError('No WhatsApp channel configured'); return; }
    setNamesDialogOpen(true);
    setNamesLoading(true);
    setNamesError(null);
    setNamesOnProvider([]);
    try {
      const res = await whatsappTemplatesApi.getTemplateNamesOnProvider(channelId);
      if (res.success && res.templates) {
        setNamesOnProvider(res.templates);
      } else {
        setNamesError('Could not load template names');
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail) && detail.length > 0
            ? (detail[0]?.msg ?? JSON.stringify(detail))
            : err?.message || 'Failed to load names';
      setNamesError(msg);
    } finally {
      setNamesLoading(false);
    }
  };

  const handleCopyNames = () => {
    const text = namesOnProvider.map((t) => t.name).join('\n');
    navigator.clipboard.writeText(text);
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-4">

      {/* ── Row 1: KPI strip + action buttons ─────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* KPI badges — computed from loaded templates */}
        <div className="flex flex-wrap gap-2">
          {kpis.map((k) => (
            <Badge
              key={k.label}
              variant="outline"
              className={cn('gap-1.5 px-3 py-1.5 text-xs font-medium', k.className)}
            >
              <k.Icon className="h-3 w-3" />
              {k.label}:{' '}
              <span className="font-semibold tabular-nums">{k.value}</span>
            </Badge>
          ))}
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleViewNamesOnWhatsApp}
            disabled={!channelId}
          >
            <List className="mr-1.5 h-3.5 w-3.5" />
            View Names
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSyncAll}
            disabled={syncing || !channelId}
          >
            <RefreshCw className={cn('mr-1.5 h-3.5 w-3.5', syncing && 'animate-spin')} />
            Sync All
          </Button>
          <Button
            size="sm"
            onClick={() => { setSelectedTemplate(null); setBuilderOpen(true); }}
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Create Template
          </Button>
        </div>
      </div>

      {/* ── Error alert ────────────────────────────────────────────────────────── */}
      {error && (
        <Alert variant="destructive" className="relative pr-10">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
          <button
            className="absolute right-3 top-3 rounded-sm opacity-70 transition-opacity hover:opacity-100"
            onClick={() => setError(null)}
            aria-label="Dismiss error"
          >
            <X className="h-4 w-4" />
          </button>
        </Alert>
      )}

      {/* ── Templates table card ────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">
            Templates
            {!loading && (
              <span className="ml-1.5 font-normal text-muted-foreground tabular-nums">
                ({templates.length})
              </span>
            )}
          </CardTitle>
          <CardAction>
            <div className="flex items-center gap-2">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  placeholder="Search templates..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="h-8 w-[180px] pl-8 text-xs"
                  aria-label="Search templates"
                />
              </div>
              {/* Status filter */}
              <Select
                value={statusFilter || 'all'}
                onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}
              >
                <SelectTrigger className="h-8 w-[120px] text-xs">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="DRAFT">Draft</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="APPROVED">Approved</SelectItem>
                  <SelectItem value="REJECTED">Rejected</SelectItem>
                </SelectContent>
              </Select>
              {/* Category filter */}
              <Select
                value={categoryFilter || 'all'}
                onValueChange={(v) => setCategoryFilter(v === 'all' ? '' : v)}
              >
                <SelectTrigger className="h-8 w-[134px] text-xs">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  <SelectItem value="UTILITY">Utility</SelectItem>
                  <SelectItem value="MARKETING">Marketing</SelectItem>
                  <SelectItem value="AUTHENTICATION">Authentication</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardAction>
        </CardHeader>

        <CardContent className="p-0 pb-2">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-6">Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="hidden sm:table-cell">Language</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right tabular-nums">Usage</TableHead>
                <TableHead className="hidden md:table-cell">Created</TableHead>
                <TableHead className="w-10 pr-6" />
              </TableRow>
            </TableHeader>

            <TableBody>
              {/* Skeleton rows while loading */}
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell className="pl-6">
                      <div className="space-y-1.5">
                        <Skeleton className="h-3.5 w-32" />
                        <Skeleton className="h-3 w-48 opacity-60" />
                      </div>
                    </TableCell>
                    <TableCell><Skeleton className="h-5 w-20 rounded-full" /></TableCell>
                    <TableCell className="hidden sm:table-cell"><Skeleton className="h-3.5 w-8" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-20 rounded-full" /></TableCell>
                    <TableCell className="text-right"><Skeleton className="ml-auto h-3.5 w-8" /></TableCell>
                    <TableCell className="hidden md:table-cell"><Skeleton className="h-3.5 w-20" /></TableCell>
                    <TableCell className="pr-6"><Skeleton className="ml-auto h-7 w-7 rounded-md" /></TableCell>
                  </TableRow>
                ))

              /* Empty state */
              ) : templates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <div className="flex flex-col items-center justify-center gap-3 py-14">
                      <div className="rounded-full bg-muted p-3">
                        <LayoutList className="h-6 w-6 text-muted-foreground" />
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium text-muted-foreground">No templates found</p>
                        <p className="mt-0.5 text-xs text-muted-foreground/70">
                          Create your first template to get started
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setSelectedTemplate(null); setBuilderOpen(true); }}
                      >
                        <Plus className="mr-1.5 h-3.5 w-3.5" />
                        Create Template
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>

              /* Data rows */
              ) : (
                paginatedTemplates.map((template) => {
                  const statusCfg  = STATUS_CONFIG[template.status];
                  const StatusIcon = statusCfg?.Icon;
                  const catCfg     = CATEGORY_CONFIG[template.category];

                  return (
                    <TableRow key={template.id} className="group">
                      {/* Name + body preview */}
                      <TableCell className="pl-6">
                        <div className="flex flex-col gap-0.5">
                          <span className="font-mono text-sm font-medium">{template.name}</span>
                          <span className="max-w-[240px] truncate text-xs text-muted-foreground">
                            {template.body.substring(0, 60)}
                            {template.body.length > 60 && '…'}
                          </span>
                        </div>
                      </TableCell>

                      {/* Category */}
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={cn('text-xs', catCfg?.className)}
                        >
                          {catCfg?.label ?? template.category}
                        </Badge>
                      </TableCell>

                      {/* Language */}
                      <TableCell className="hidden text-sm text-muted-foreground sm:table-cell">
                        {template.language}
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        {statusCfg ? (
                          <Badge variant="outline" className={cn('gap-1 text-xs', statusCfg.className)}>
                            {StatusIcon && <StatusIcon className="h-3 w-3" />}
                            {statusCfg.label}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">{template.status}</Badge>
                        )}
                      </TableCell>

                      {/* Usage count */}
                      <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
                        {template.usage_count}
                      </TableCell>

                      {/* Created date */}
                      <TableCell className="hidden text-sm text-muted-foreground md:table-cell">
                        {new Date(template.created_at).toLocaleDateString()}
                      </TableCell>

                      {/* Per-row action menu — no shared anchorEl state needed */}
                      <TableCell className="pr-6 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 opacity-0 transition-opacity group-hover:opacity-100"
                              aria-label={`Actions for ${template.name}`}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem
                              onClick={() => { setSelectedTemplate(template); setBuilderOpen(true); }}
                              disabled={template.status === WhatsAppTemplateStatus.APPROVED}
                            >
                              <Pencil className="mr-2 h-3.5 w-3.5" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleSubmitForApproval(template)}
                              disabled={template.status !== WhatsAppTemplateStatus.DRAFT}
                            >
                              <Send className="mr-2 h-3.5 w-3.5" />
                              Submit for Approval
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleSyncStatus(template)}
                              disabled={template.status === WhatsAppTemplateStatus.DRAFT}
                            >
                              <RefreshCw className="mr-2 h-3.5 w-3.5" />
                              Sync Status
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => { setTemplateToDelete(template); setDeleteDialogOpen(true); }}
                            >
                              <Trash2 className="mr-2 h-3.5 w-3.5" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t px-6 py-3">
              <p className="text-xs text-muted-foreground">
                Showing{' '}
                <span className="font-medium tabular-nums">
                  {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, templates.length)}
                </span>{' '}
                of{' '}
                <span className="font-medium tabular-nums">{templates.length}</span>
              </p>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setPage(1)} disabled={page === 1} aria-label="First page">
                  <ChevronsLeft className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setPage((p) => p - 1)} disabled={page === 1} aria-label="Previous page">
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <span className="min-w-[5rem] text-center text-xs tabular-nums text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setPage((p) => p + 1)} disabled={page === totalPages} aria-label="Next page">
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setPage(totalPages)} disabled={page === totalPages} aria-label="Last page">
                  <ChevronsRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Template Builder — Sheet (slide-over) ──────────────────────────────── */}
      <Sheet
        open={builderOpen}
        onOpenChange={(open) => {
          if (!open) { setBuilderOpen(false); setSelectedTemplate(null); }
        }}
      >
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-[min(100vw-1rem,56rem)]"
        >
          <SheetHeader className="shrink-0 space-y-1.5 border-b border-border px-6 py-3.5 pr-14 text-left">
            <SheetTitle>
              {selectedTemplate ? 'Edit Template' : 'Create New Template'}
            </SheetTitle>
            <SheetDescription>
              {selectedTemplate
                ? `Editing "${selectedTemplate.name}"`
                : 'Build a new WhatsApp message template for your customers'}
            </SheetDescription>
          </SheetHeader>
          <ScrollArea className="min-h-0 flex-1">
            <div className="px-6 py-5">
              <TemplateBuilder
                accountId={accountId}
                channelId={channelId}
                initialTemplate={selectedTemplate || undefined}
                onSave={selectedTemplate ? handleUpdateTemplate : handleCreateTemplate}
                onCancel={() => { setBuilderOpen(false); setSelectedTemplate(null); }}
              />
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* ── Delete confirmation — AlertDialog ──────────────────────────────────── */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{' '}
              <span className="font-mono font-medium text-foreground">
                &ldquo;{templateToDelete?.name}&rdquo;
              </span>
              ? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDeleteTemplate}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Names on WhatsApp — Dialog ─────────────────────────────────────────── */}
      <Dialog open={namesDialogOpen} onOpenChange={setNamesDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Template names on your WhatsApp account</DialogTitle>
            <DialogDescription>
              These names are already in use. When creating a new template, use a name not in this list.
            </DialogDescription>
          </DialogHeader>

          {namesLoading ? (
            <div className="space-y-2 py-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          ) : namesError ? (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{namesError}</AlertDescription>
            </Alert>
          ) : namesOnProvider.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">
              No templates on WhatsApp yet, or could not load. Try &ldquo;Sync All&rdquo; first.
            </p>
          ) : (
            <div className="max-h-[360px] overflow-y-auto rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="pl-4">Name</TableHead>
                    <TableHead>Language</TableHead>
                    <TableHead className="pr-4">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {namesOnProvider.map((t, i) => (
                    <TableRow key={`${t.name}-${t.language}-${i}`}>
                      <TableCell className="pl-4 font-mono text-sm">{t.name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{t.language}</TableCell>
                      <TableCell className="pr-4 text-sm text-muted-foreground">{t.status || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <DialogFooter className="sm:justify-between">
            {namesOnProvider.length > 0 && (
              <Button variant="outline" size="sm" onClick={handleCopyNames}>
                <Copy className="mr-1.5 h-3.5 w-3.5" />
                Copy names (one per line)
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setNamesDialogOpen(false)}
              className="ml-auto"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default TemplateManagement;
